import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const isQuotaError = this.state.error?.message.includes('Quota exceeded') || 
                           this.state.error?.message.includes('resource-exhausted');

      return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100 p-8">
          <div className="max-w-md w-full space-y-4">
            <h1 className="text-2xl font-mono uppercase tracking-widest text-red-500">
              {isQuotaError ? 'Bandwidth Scarcity' : 'System Error'}
            </h1>
            <p className="text-zinc-400 font-sans">
              {isQuotaError 
                ? "The laboratory has exceeded its daily free compute quota. This usually resets within 24 hours at 00:00 PST." 
                : "An unexpected error occurred in the studio environment."}
            </p>
            {isQuotaError ? (
              <div className="bg-zinc-900 p-4 rounded border border-zinc-800 space-y-2">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest leading-relaxed">
                  Detailed quota information can be found in the Firebase Console. You may need to wait for the daily reset or consider optimizing data retrieval patterns in non-production environments.
                </p>
                <a 
                  href="https://firebase.google.com/pricing#cloud-firestore" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block text-[10px] font-mono text-blue-400 hover:text-blue-300 underline uppercase"
                >
                   View Pricing & Limits
                </a>
              </div>
            ) : (
              <pre className="bg-zinc-900 p-4 rounded border border-zinc-800 overflow-auto text-xs font-mono text-zinc-500">
                {this.state.error?.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 bg-zinc-100 text-zinc-950 font-mono uppercase text-xs tracking-widest hover:bg-zinc-300 transition-colors"
            >
              Restart Environment
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
